import { Popover } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateView } from '@mui/x-date-pickers/models';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { Moment } from 'moment';
import { useState } from 'react';

import {
  parseStoredCustomFieldDate,
  toStoredCustomFieldDate,
} from './customFieldDate';

interface Props {
  anchorEl: HTMLElement | null;
  value: unknown;
  // Empty string means the value was cleared.
  onChange: (value: string) => void;
  onClose: () => void;
}

// Mounted only while the popover is open, so each open starts on the day view with the stored value.
const CustomFieldDatePicker = ({
  value,
  onChange,
  onClose,
}: Omit<Props, 'anchorEl'>) => {
  const [view, setView] = useState<DateView>('day');

  // Picking a year also fires onChange, before the user has chosen a day, so only a day pick or Clear saves.
  const handleChange = (date: Moment | null) => {
    if (date && (view !== 'day' || !date.isValid())) return;
    onChange(date ? toStoredCustomFieldDate(date) : '');
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StaticDatePicker
        defaultValue={parseStoredCustomFieldDate(value)}
        onChange={handleChange}
        onViewChange={setView}
        slotProps={{
          actionBar: { actions: ['clear'] },
          toolbar: { hidden: true },
        }}
      />
    </LocalizationProvider>
  );
};

// The one date picker for custom field values: the record, and the CRM table.
export const CustomFieldDatePopover = ({
  anchorEl,
  value,
  onChange,
  onClose,
}: Props) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
  >
    {Boolean(anchorEl) && (
      <CustomFieldDatePicker
        value={value}
        onChange={onChange}
        onClose={onClose}
      />
    )}
  </Popover>
);
